import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import { disableAction, listActions } from "@/api/actions";
import type { ActionSummary } from "@/api/types";
import { Page } from "@/components/page";
import { EmptyState } from "@/components/empty-state";

export function ActionsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const actions = useQuery({
    queryKey: ["actions"],
    queryFn: listActions,
  });

  const disable = useMutation({
    mutationFn: (a: ActionSummary) => disableAction(a.id),
    onSuccess: (updated) => {
      toast.success(`${updated.name} disabled`);
      qc.invalidateQueries({ queryKey: ["actions"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          "Can't disable: this action is still referenced by an enabled check.",
        );
        return;
      }
      toast.error((err as Error).message);
    },
  });

  const items = actions.data ?? [];

  return (
    <Page
      title="Actions"
      actions={
        <button
          className="btn-primary"
          onClick={() =>
            navigate({ to: "/actions/$actionId", params: { actionId: "new" } })
          }
        >
          <Plus className="h-4 w-4" /> New action
        </button>
      }
    >
      <section className="card overflow-hidden">
        {items.length === 0 && !actions.isLoading ? (
          <EmptyState
            title="No actions yet"
            hint="Auto-seed runs on api boot; if you're seeing this, the seed pass failed."
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="text-xs text-muted">
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Kind</th>
                <th className="px-4 py-2 text-left font-medium">Config</th>
                <th className="px-4 py-2 text-left font-medium">Enabled</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((a) => (
                <tr key={a.id} className="hover:bg-subtle/40">
                  <td className="px-4 py-2">
                    <Link
                      to="/actions/$actionId"
                      params={{ actionId: a.id }}
                      className="text-accent hover:underline"
                    >
                      {a.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <span className="badge text-[10px]">{a.spec.kind}</span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted">
                    {a.spec.kind === "extract"
                      ? a.spec.close_opp_status_id
                      : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {a.enabled ? (
                      <span className="badge-success text-[10px]">on</span>
                    ) : (
                      <span className="badge text-[10px] text-muted">off</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {a.enabled ? (
                      <button
                        className="btn-ghost text-xs"
                        onClick={() => {
                          if (confirm(`Disable action "${a.name}"?`)) {
                            disable.mutate(a);
                          }
                        }}
                        disabled={disable.isPending}
                        title="Disable (refuses if any enabled check still uses it)"
                      >
                        <PowerOff className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </Page>
  );
}
